import { useState, useEffect } from 'react'
import {
  useAttachmentUploading,
  useLastUserMessage,
  useModels,
  useStreamingMetadataForChat,
  useReadAllChatMessages,
  useActiveChatId,
  useIsActiveTurn,
} from '../../../../store'
import { MetadataEvent, UserMessage } from '../../../../types'
import { getModelIdForMessage } from '../../../../utils/chat'
import { useGlobalStore } from '@highlight/ipc/react'
import { LoadingText } from '../../../elements/LoadingText'
import { twMerge } from 'tailwind-merge'
import { FaviconItem } from '../favicon-item'
import { useModelChoice } from '../../../../hooks/use-plan-features'
import { unsecureRandom } from '@shared/utils/random'

const thinkingMessages = ['Thinking...']

export const MessageMetadata = () => {
  const activeChatId = useActiveChatId()
  const streamingMetadata = useStreamingMetadataForChat(activeChatId)
  const isChatLoading = useIsActiveTurn()
  const { isAttachmentUploading } = useAttachmentUploading()
  const [currentMetadata, setCurrentMetadata] = useState<MetadataEvent>()
  const [thinkingMessage] = useState(() => thinkingMessages[Math.floor(unsecureRandom() * thinkingMessages.length)])
  const [showInitialMessage, setShowInitialMessage] = useState(true)
  const [thinkingDuration, setThinkingDuration] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const lastUserMessage = useLastUserMessage('user') as UserMessage
  const models = useModels()
  // Subscribed rather than read synchronously in render — the sync IPC `get`
  // ran on every one of this component's streaming re-renders.
  const [defaultModel] = useGlobalStore<string>('settings', 'defaultModel')
  // A read, not a subscription: this only feeds the model label below, and the
  // component already re-renders on the metadata events that can change it.
  const readAllChatMessages = useReadAllChatMessages()
  const modelChoice = useModelChoice()

  // Reset states when streaming stops
  useEffect(() => {
    if (!isChatLoading) {
      setCurrentMetadata(undefined)
      setShowInitialMessage(true)
      setThinkingDuration(0)
    }
  }, [isChatLoading])

  useEffect(() => {
    if (currentMetadata?.search === true) {
      setHasSearched(true)
    }
  }, [currentMetadata?.search])

  useEffect(() => {
    if (isChatLoading) {
      setHasSearched(false)
    }
  }, [isChatLoading])

  // Handle thinking timer
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>

    if (
      isChatLoading &&
      (streamingMetadata?.model === 'o1' ||
        lastUserMessage?.mentions.some((mention) => mention.type === 'model' && mention.label === 'O1'))
    ) {
      intervalId = setInterval(() => {
        setThinkingDuration((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId)
      }
    }
  }, [isChatLoading, streamingMetadata?.model, lastUserMessage])

  // Handle metadata updates only when streaming
  useEffect(() => {
    if (isChatLoading && !showInitialMessage) {
      setCurrentMetadata(streamingMetadata)
    }
  }, [streamingMetadata, isChatLoading, showInitialMessage])

  // Handle initial message timer
  useEffect(() => {
    if (isChatLoading) {
      setShowInitialMessage(true)
      const timer = setTimeout(() => {
        setShowInitialMessage(false)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isChatLoading])

  const getMessage = () => {
    if (!isChatLoading) return null
    if (isAttachmentUploading) return 'Uploading...'
    if (showInitialMessage) return thinkingMessage

    const mentions = lastUserMessage?.mentions ?? []

    const selectedModelId = activeChatId
      ? getModelIdForMessage({
          mentions,
          isExistingChat: !!activeChatId,
          conversationId: activeChatId,
          allMessages: readAllChatMessages(),
          defaultModel,
          useAdvancedReasoning: false,
          modelChoice,
        })
      : undefined

    // Find the selected model in the models list
    const selectedModel = selectedModelId ? models.find((model) => model.id === selectedModelId) : undefined

    // Special case for O1 model which shows thinking time
    if (selectedModel?.label === 'O1' || currentMetadata?.model === 'o1') {
      const seconds = thinkingDuration % 60
      const minutes = Math.floor(thinkingDuration / 60) % 60
      const hours = Math.floor(thinkingDuration / 3600)
      const timeString = `${hours ? `${hours}h ` : ''}${minutes ? `${minutes}m ` : ''}${seconds}s`
      return `Reasoning for ${timeString}...`
    }

    // Show selected model if available
    if (selectedModel) {
      return `Using ${selectedModel.label}...`
    }

    // Fall back to metadata-based indicators
    if (!currentMetadata) return thinkingMessage

    if (currentMetadata?.search === true) {
      return 'Searching the web...'
    }

    if (currentMetadata?.knowledge_used) {
      return 'Checking knowledge base...'
    }

    if (currentMetadata?.favicons && currentMetadata.favicons.length > 0) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {currentMetadata.favicons?.map((favicon, index) => (
              <FaviconItem
                key={index}
                src={favicon}
                className={twMerge('h-4 w-4', index > 0 && '-ml-1')}
                showFallback={false}
                zIndex={(currentMetadata.favicons?.length || 0) - index}
                shadow={index < (currentMetadata.favicons?.length || 0) - 1}
              />
            ))}
          </div>
        </div>
      )
    }

    if (currentMetadata?.audio_notes_search === true) {
      return 'Searching audio notes...'
    }

    if (hasSearched) {
      return (
        <div className="text-light-60 flex items-center gap-2 font-normal">
          <span>Searched the web</span>
        </div>
      )
    }

    if (currentMetadata?.to_provider ?? currentMetadata?.model) {
      const target = currentMetadata.to_provider ?? currentMetadata.model
      return `Using ${target}...`
    }

    return thinkingMessage
  }

  // Handle search timeout
  useEffect(() => {
    if (currentMetadata?.search === true) {
      const timer = setTimeout(() => {
        setCurrentMetadata((prev) => (prev ? { ...prev, search: false } : undefined))
      }, 8000)
      return () => clearTimeout(timer)
    }
  }, [currentMetadata?.search])

  const message = getMessage()

  if (!message) return null

  // Handle React element case
  if (typeof message !== 'string') {
    return <span className="text-light-40 font-normal">{message}</span>
  }

  return <LoadingText text={message} className="text-light-40 font-normal" />
}

export default MessageMetadata
